/* A deliberately small rich-text field: bold, italic, bullets, links.

   Built on contentEditable rather than an editor library because the output
   has to survive worker/experts.ts cleanBio, which keeps exactly these tags and
   drops everything else. An editor that can produce headings, colours or
   embeds would let someone write things the server then silently removes.

   Paste is plain text for the same reason: formatting copied from Word or a
   website arrives as spans and styles the server would strip anyway, and the
   bio would look different after saving than before. Links are added with the
   Link button, which is the one place their URL gets checked. */
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/lib/icons';

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Bios saved before they were HTML are plain text; show them as paragraphs. */
function toEditorHtml(value: string): string {
  if (!value) return '';
  if (/<[a-z/]/i.test(value)) return value;
  return value.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    .map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');
}

const isBlank = (html: string) => !html.replace(/<br\s*\/?>|<\/?(p|div)>|&nbsp;|\s/gi, '');

/** "example.com" → https://example.com, "a@b.com" → mailto:. Null when it can't be a link. */
export function normalizeLink(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (/^mailto:/i.test(v)) return v;
  if (/^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(v)) return `mailto:${v}`;
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;
  }
}

export function RichTextEditor({ label, hint, value, onChange, placeholder }: {
  label: string;
  hint?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // What this editor last emitted. The DOM is only rewritten when `value`
  // changes from OUTSIDE — rewriting it on every keystroke jumps the caret.
  const emitted = useRef<string | null>(null);
  const saved = useRef<Range | null>(null);
  const [linking, setLinking] = useState(false);
  const [url, setUrl] = useState('');
  const [linkErr, setLinkErr] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el || value === emitted.current) return;
    el.innerHTML = toEditorHtml(value);
    emitted.current = value;
  }, [value]);

  const emit = () => {
    const html = ref.current?.innerHTML ?? '';
    const v = isBlank(html) ? '' : html;
    emitted.current = v;
    onChange(v);
  };

  const run = (cmd: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const selectionInside = () => {
    const sel = window.getSelection();
    return sel && sel.rangeCount > 0 && ref.current?.contains(sel.anchorNode) ? sel : null;
  };

  const startLink = () => {
    const sel = selectionInside();
    saved.current = sel ? sel.getRangeAt(0).cloneRange() : null;
    setUrl(''); setLinkErr(''); setLinking(true);
  };

  const applyLink = () => {
    const href = normalizeLink(url);
    if (!href) { setLinkErr('Enter a web address or an email.'); return; }
    ref.current?.focus();
    const sel = window.getSelection();
    if (sel && saved.current) { sel.removeAllRanges(); sel.addRange(saved.current); }
    if (sel && !sel.isCollapsed && ref.current?.contains(sel.anchorNode)) {
      document.execCommand('createLink', false, href);
    } else {
      // Nothing selected: insert the address itself as the link text.
      const text = href.replace(/^mailto:/i, '');
      document.execCommand('insertHTML', false, `<a href="${esc(href)}">${esc(text)}</a>&nbsp;`);
    }
    setLinking(false);
    emit();
  };

  return (
    <div className="tr-field">
      <span className="tr-field__label">{label}</span>
      <div className="tr-field__control rte">
        <div className="rte__bar" role="toolbar" aria-label={`${label} formatting`}>
          {/* onMouseDown + preventDefault keeps the selection in the editor. */}
          <button type="button" className="rte__btn" title="Bold (Ctrl+B)" onMouseDown={(e) => { e.preventDefault(); run('bold'); }}>
            <strong>B</strong>
          </button>
          <button type="button" className="rte__btn" title="Italic (Ctrl+I)" onMouseDown={(e) => { e.preventDefault(); run('italic'); }}>
            <em>I</em>
          </button>
          <button type="button" className="rte__btn" title="Bulleted list" onMouseDown={(e) => { e.preventDefault(); run('insertUnorderedList'); }}>
            <Icon name="list" size={15} />
          </button>
          <button type="button" className="rte__btn" title="Add link" onMouseDown={(e) => { e.preventDefault(); startLink(); }}>
            <Icon name="link" size={15} />
          </button>
          <button type="button" className="rte__btn" title="Remove link" onMouseDown={(e) => { e.preventDefault(); run('unlink'); }}>
            <Icon name="link" size={15} /><Icon name="x" size={11} />
          </button>
        </div>
        {linking && (
          <div className="rte__link">
            <input
              className="tr-input" autoFocus placeholder="https://… or name@example.com" value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                if (e.key === 'Escape') { e.preventDefault(); setLinking(false); }
              }}
            />
            <button type="button" className="rte__btn rte__btn--text" onClick={applyLink}>Add</button>
            <button type="button" className="rte__btn rte__btn--text" onClick={() => setLinking(false)}>Cancel</button>
            {linkErr && <span className="rte__err">{linkErr}</span>}
          </div>
        )}
        <div
          ref={ref}
          className="rte__area"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          data-placeholder={placeholder}
          onFocus={() => document.execCommand('defaultParagraphSeparator', false, 'p')}
          onInput={emit}
          onPaste={(e) => {
            e.preventDefault();
            document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
          }}
        />
      </div>
      {hint && <span className="tr-field__hint">{hint}</span>}
    </div>
  );
}
